import Link from 'next/link';
import { Banknote, Haze, MapPin } from 'lucide-react';
import { Card } from '@upzi/web-shell/ui/card';
import { getCompanyProfilePath } from '@/services/company-profile/routes';
import { getJobDetailPath } from '@/services/jobs';
import type { SimilarJob } from '@/services/jobs/model';
import { formatJobLocation } from '../utils';
import { CompanyLogo } from './company-logo';
import { JobDetailLink } from './job-detail-link';
import { JobSaveButton } from './job-save-button';

type SimilarJobCardProps = {
  isSavePending?: boolean;
  isSaved?: boolean;
  job: SimilarJob;
  onSaveChange?: (job: SimilarJob) => void;
  requiresLogin?: boolean;
};

export function SimilarJobCard({
  isSavePending = false,
  isSaved = false,
  job,
  onSaveChange,
  requiresLogin = false,
}: SimilarJobCardProps) {
  const detailPath = getJobDetailPath(job);
  const companyHref = getCompanyProfilePath(job.company);

  return (
    <Card className="border-alias-outline bg-surface-pure has-[.similar-job-card-action:focus-visible]:border-primary has-[.similar-job-card-action:focus-visible]:ring-primary/50 hover:border-on-surface hover:bg-surface-pure relative h-[186px] w-full min-w-0 overflow-hidden rounded-[18px] border p-0 py-5 shadow-none transition-[border-color,background-color] duration-200 has-[.similar-job-card-action:focus-visible]:ring-3">
      <JobDetailLink
        href={detailPath}
        aria-label={`Xem chi tiết ${job.jobTitle}`}
        className="similar-job-card-action absolute inset-0 z-0 rounded-[18px] focus-visible:outline-none focus-visible:ring-inset"
      >
        <span className="sr-only">Xem chi tiết {job.jobTitle}</span>
      </JobDetailLink>

      <div className="pointer-events-none relative z-10 flex h-14 items-start gap-3 px-5">
        <Link
          href={companyHref}
          target="_blank"
          rel="noopener"
          aria-label={`Mở trang công ty ${job.company.name || 'Nhà tuyển dụng'} trong tab mới`}
          className="focus-visible:ring-primary/50 pointer-events-auto shrink-0 rounded-xl focus-visible:ring-2 focus-visible:outline-none"
        >
          <CompanyLogo job={job} size={56} imageClassName="p-0" />
        </Link>
        <div className="min-w-0 flex-1">
          <h3 className="text-on-surface text-base leading-6 font-semibold">
            <JobDetailLink
              href={detailPath}
              className="text-on-surface hover:text-on-surface focus-visible:ring-primary/50 pointer-events-auto block truncate rounded-sm underline-offset-2 hover:underline focus-visible:ring-3 focus-visible:outline-none"
            >
              {job.jobTitle}
            </JobDetailLink>
          </h3>
          <Link
            href={companyHref}
            target="_blank"
            rel="noopener"
            className="text-on-surface hover:text-on-surface focus-visible:ring-primary/50 pointer-events-auto block truncate rounded-sm text-base leading-[22px] font-normal underline-offset-2 hover:underline focus-visible:ring-3 focus-visible:outline-none"
          >
            {job.company.name}
          </Link>
        </div>
        <JobSaveButton
          variant="ghost"
          className="hover:bg-gray-05 -mr-2 shadow-none"
          isPending={isSavePending}
          isSaved={isSaved}
          onSaveChange={onSaveChange ? () => onSaveChange(job) : undefined}
          requiresLogin={requiresLogin}
        />
      </div>

      <div className="border-alias-outline pointer-events-none absolute inset-x-0 top-[84px] z-10 border-t" />

      <div className="text-on-surface pointer-events-none relative z-10 mt-4 flex h-[74px] flex-col gap-1 px-5 text-base leading-[22px] font-normal">
        <p className="flex min-w-0 items-center gap-2">
          <Banknote className="size-4 shrink-0" aria-hidden />
          <span className="truncate">{job.prettySalary}</span>
        </p>
        <p className="flex min-w-0 items-center gap-2">
          <MapPin className="size-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate">
            {formatJobLocation(job)}
          </span>
        </p>
        <p className="flex min-w-0 items-center gap-2">
          <Haze className="size-4 shrink-0" aria-hidden />
          <span className="truncate">{job.workingOnWeekendText}</span>
        </p>
      </div>
    </Card>
  );
}
